import axios from "axios";
import { App } from "../environments/environment";

export interface PortfolioProfile {
  id: string;
  user_id: string;
  full_name: string;
  what_i_do: string;
  title: string;
  bio: string;
  avatar_url: string;
  email: string;
  github_url: string;
  linkedin_url: string;
  website_url: string;
  whatsapp: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSkill {
  id: string;
  user_id: string;
  title: string;
  description: string;
  since_date: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioExperience {
  id: string;
  user_id: string;
  title: string;
  description: string;
  company: string;
  location: string;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

export interface PortfolioFormation {
  id: string;
  user_id: string;
  title: string;
  description: string;
  institution: string;
  degree: string;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioProduct {
   id: string;
   user_id: string;
   title: string;
   slug?: string;
   description: string;
   details?: string;
   image_url: string;
   repo_url?: string;
   live_url?: string;
   technologies?: string[];
   deploy_at?: string;
}

export interface PortfolioBlog {
    id: string;
    user_id: string;
    title: string;
    description: string;
    content: string;
    slug: string;
}

export interface PortfolioData {
  profile: PortfolioProfile;
  skills: PortfolioSkill[];
  experiences: PortfolioExperience[];
  formations: PortfolioFormation[];
  products: PortfolioProduct[];
  blogs: PortfolioBlog[];
}

export interface PortfolioResponse {
  status_code: number;
  message: string | null;
  data: PortfolioData;
}

export const BaseURL = App.API;

export interface TranslationContent {
  lang: string;
  value: string;
}

export interface TranslationData {
  id: number;
  key: string;
  type: string;
  contents: TranslationContent[];
}

export interface TranslationResponse {
  status_code: number;
  message: string | null;
  data: TranslationData;
}

export const getTranslationByKey = async (key: string): Promise<TranslationResponse | null> => {
  try {
    const response = await axios.get<TranslationResponse>(
      `${App.TRANSLATION_API}/translations/${key}`
    );
    return response.data;
  } catch (err) {
    console.log("Error fetching translation:", err);
    return null;
  }
}

export interface SendEmailResponse {
    status_code: number;
    message: string | null;
}

export const sendEmail = async (
    email: string,
    telephone: string,
    content: string,
    userId: string = App.PORTFOLIO_USER_ID
): Promise<SendEmailResponse | null> => {
    try {
        const response = await axios.post<SendEmailResponse>(
            `${App.API}/send-email`,
            {
                user_id: userId,
                email,
                telephone,
                content,
            }
        );
        return response.data;
    } catch (err) {
        console.log("Error sending email:", err);
        return null;
    }
}

export const getPortfolio = async (userId: string): Promise<PortfolioData | null> => {
    try {
        const response = await axios.get<PortfolioResponse>(
            `https://portfolio-api.bemanjary.com/api/v1/portfolio/${userId}`
        );
        return response.data.data;
    } catch (err) {
        console.log("Error fetching portfolio:", err);
        return null;
    }
}

export interface ProductsResponse {
    status_code: number;
    message: string | null;
    data: {
        count: number;
        data: PortfolioProduct[];
    };
}

export interface BlogsResponse {
    status_code: number;
    message: string | null;
    data: {
        count: number;
        data: PortfolioBlog[];
    };
}

export const getProducts = async (
    userId: string,
    skip: number = 0,
    limit: number = 12,
    query?: string
): Promise<ProductsResponse | null> => {
    try {
        const params = new URLSearchParams({
            user_id: userId,
            skip: skip.toString(),
            limit: limit.toString(),
            deployed_only: "true",
        });

        if (query) {
            params.append('query', query);
        }

        const response = await axios.get<ProductsResponse>(
            `https://portfolio-api.bemanjary.com/api/v1/products?${params.toString()}`
        );
        return response.data;
    } catch (err) {
        return null;
    }
}

export interface ProductDetailResponse {
    status_code: number;
    message: string | null;
    data: PortfolioProduct;
}

export const getProductBySlug = async (slug: string): Promise<ProductDetailResponse | null> => {
    try {
        const response = await axios.get<ProductDetailResponse>(
            `https://portfolio-api.bemanjary.com/api/v1/products/${slug}`
        );
        return response.data;
    } catch (err) {
        return null;
    }
}

export const getBlogs = async (
    userId: string,
    skip: number = 0,
    limit: number = 10,
    query?: string
): Promise<BlogsResponse | null> => {
    try {
        const params = new URLSearchParams({
            user_id: userId,
            skip: skip.toString(),
            limit: limit.toString(),
        });

        if (query) {
            params.append('query', query);
        }

        const response = await axios.get<BlogsResponse>(
            `https://portfolio-api.bemanjary.com/api/v1/blogs/published?${params.toString()}`
        );
        return response.data;
    } catch (err) {
        return null;
    }
}

export interface BlogDetailResponse {
    status_code: number;
    message: string | null;
    data: PortfolioBlog;
}

export const getBlogBySlug = async (slug: string): Promise<BlogDetailResponse | null> => {
    try {
        const response = await axios.get<BlogDetailResponse>(
            `https://portfolio-api.bemanjary.com/api/v1/blogs/${slug}`
        );
        return response.data;
    } catch (err) {
        return null;
    }
}